// ============================================
// CSV Export Utility
// Generates CSV and triggers download or opens in Google Sheets
// ============================================

type Row = Record<string, string | number | boolean | string[] | undefined>;

/** Escape a CSV cell value */
function escapeCell(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (Array.isArray(value)) {
    const joined = value.join('; ');
    return `"${joined.replace(/"/g, '""')}"`;
  }
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/** Convert array of objects to CSV string */
export function toCSV(rows: Row[], columns: { key: string; label: string }[]): string {
  const header = columns.map((c) => escapeCell(c.label)).join(',');
  const body = rows.map((row) =>
    columns.map((c) => escapeCell(row[c.key])).join(','),
  );
  return [header, ...body].join('\n');
}

/** Trigger a CSV file download */
export function downloadCSV(csv: string, filename: string): void {
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' }); // BOM for Excel compat
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

/** Open CSV data in a new Google Sheet */
export function openInGoogleSheets(csv: string, title: string): void {
  // Create a Blob URL, then use Google Sheets' import from HTML approach
  // Since we can't directly open a local CSV in Sheets, we:
  // 1. Copy to clipboard as TSV (tab-separated, Sheets pastes this natively)
  // 2. Open a new Google Sheet
  // 3. User pastes with Ctrl+V

  // Convert CSV to TSV for clipboard (Sheets handles TSV paste perfectly)
  const tsv = csvToTsv(csv);

  navigator.clipboard.writeText(tsv).then(() => {
    // Open a new blank Google Sheet
    window.open(
      `https://docs.google.com/spreadsheets/create?title=${encodeURIComponent(title)}`,
      '_blank',
    );
    alert('Data copied to clipboard! In the new Google Sheet, press Ctrl+V (or Cmd+V) to paste your data.');
  }).catch(() => {
    // Fallback: just download CSV
    downloadCSV(csv, `${title}.csv`);
    alert('Could not copy to clipboard. CSV file downloaded instead — open it in Google Sheets.');
  });
}

/** Convert CSV to TSV (for clipboard paste into Sheets) */
function csvToTsv(csv: string): string {
  const rows = parseCSVRows(csv);
  return rows.map((row) => row.join('\t')).join('\n');
}

/** Simple CSV parser that handles quoted fields */
function parseCSVRows(csv: string): string[][] {
  const rows: string[][] = [];
  let current: string[] = [];
  let cell = '';
  let inQuotes = false;

  for (let i = 0; i < csv.length; i++) {
    const ch = csv[i];
    if (inQuotes) {
      if (ch === '"' && csv[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        cell += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ',') {
        current.push(cell);
        cell = '';
      } else if (ch === '\n') {
        current.push(cell);
        rows.push(current);
        current = [];
        cell = '';
      } else {
        cell += ch;
      }
    }
  }
  if (cell || current.length > 0) {
    current.push(cell);
    rows.push(current);
  }
  return rows;
}
